/**
 * useScheduleDraftEditorMutations — ScheduleDraft 工作台写操作编排入口。
 *
 * 本 hook 组合航班、轨、草稿更新、参考轨挂载与来源导入 mutation，并把成功
 * 提示与 UI 回调留在编辑器边界。底层请求和 payload 细节继续下沉到 services
 * 与专用 mutation hook。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → hooks/useScheduleDraftFlightMutations.js (航班写入), hooks/useScheduleDraftTrackMutations.js (轨写入), services/scheduleDraftPayloadBuilders.js (payload 组装)
 */

import { message } from "antd";
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { scheduleDraftQueryKeys } from "./queryKeys";
import { useScheduleDraftMutationHelper } from "./useScheduleDraftMutationHelper";
import { useImportScheduleDraftSourceFlights, useImportScheduleDraftTableFlights, useMoveScheduleDraftFlight, useUpdateScheduleDraftFlight } from "./useScheduleDraftFlightMutations";
import { useRestoreTrack, useSaveTrackAsHistory, useCreateTrack, useFinalizeTrack, useDeleteTrack } from "./useScheduleDraftTrackMutations";
import { formatScheduleDraftError } from "../lib/errorUtils";
import { ADD_FLIGHT, DELETE_FLIGHT } from "../lib/editCommand";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";
import { buildScheduleDraftFlightPayload, buildScheduleDraftSourceFlightsPayload } from "../services/scheduleDraftPayloadBuilders";

function createClientFlightId() {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) => (
        Number(char) ^ Math.floor(Math.random() * 16) & (15 >> (Number(char) / 4))
    ).toString(16));
}

function normalizeDateTimeValues(values = {}) {
    return {
        ...values,
        std: values.std?.toISOString ? values.std.toISOString() : values.std,
        sta: values.sta?.toISOString ? values.sta.toISOString() : values.sta,
    };
}

function getFirstBlockingMessage(result, fallback) {
    return result?.issues?.find((issue) => issue?.blocking)?.message || result?.issues?.[0]?.message || fallback;
}

export function useScheduleDraftEditorMutations({ draftId, defaultTrackId, currentEditingTrackId, editPipeline, hasLocalWorkingChanges = false, onAddFlightSuccess, onDeleteFlightSuccess, onDeleteTrackSuccess, onRestoreTrackSuccess, onAttachReferenceTrackSuccess, onImportSourceFlightsSuccess, onImportTableFlightsSuccess, onDraftFlightsChanged }) {
    const queryClient = useQueryClient();
    const [addingLocalFlight, setAddingLocalFlight] = useState(false);
    const [deletingLocalFlight, setDeletingLocalFlight] = useState(false);
    const effectiveEditingTrackId = currentEditingTrackId || defaultTrackId;
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
    });

    const guardServerMutation = useCallback(
        (actionLabel) => {
            if (!hasLocalWorkingChanges) {
                return true;
            }
            message.warning(`请先保存或放弃本地修改后再${actionLabel}`);
            return false;
        },
        [hasLocalWorkingChanges],
    );

    const updateFlightMutation = useUpdateScheduleDraftFlight(draftId, {
        onSuccess: (flight) => {
            message.success(`已更新航班 ${flight.flight_number || ""}`.trim());
            onDraftFlightsChanged?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "更新航班失败"));
        },
    });

    const moveFlightMutation = useMoveScheduleDraftFlight(draftId, {
        onSuccess: (flight) => {
            message.success(`已移动航班 ${flight.flight_number || ""}`.trim());
            onDraftFlightsChanged?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "移动航班失败"));
        },
    });

    const attachReferenceTrackMutation = useMutation(
        (values) => scheduleDraftDataService.attachSourceReferenceTrack(draftId, buildScheduleDraftSourceFlightsPayload(values)),
        buildMutationOptions({
            onSuccess: (track) => {
                message.success(`已创建参考轨 ${track.name || ""}`.trim());
                onAttachReferenceTrackSuccess?.(track);
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "创建参考轨失败"));
            },
        }),
    );

    const importSourceFlightsMutation = useImportScheduleDraftSourceFlights(draftId, {
        onSuccess: (flights, variables) => {
            message.success(`已导入 ${flights.length || 0} 个航班`);
            onImportSourceFlightsSuccess?.(flights, {
                targetTrackId: variables?.target_track_id || variables?.targetTrackId,
            });
            onDraftFlightsChanged?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "导入来源航班失败"));
        },
    });

    const importTableFlightsMutation = useImportScheduleDraftTableFlights(draftId, {
        onSuccess: (flights, variables) => {
            message.success(`已导入 ${flights.length || 0} 个表格航班`);
            onImportTableFlightsSuccess?.(flights, {
                targetTrackId: variables?.values?.target_track_id || variables?.values?.targetTrackId,
            });
            onDraftFlightsChanged?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "导入表格航班失败"));
        },
    });

    const createTrackMutation = useCreateTrack(draftId, {
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "创建编辑轨失败"));
        },
    });

    const handleImportSourceFlights = useCallback(
        async (values) => {
            if (!guardServerMutation("导入/挂载")) {
                return null;
            }
            const newTrackName = (values.new_track_name || values.newTrackName || "").trim();
            if (newTrackName) {
                const track = await createTrackMutation.mutateAsync({
                    name: newTrackName,
                    track_type: "EDITING",
                });
                return importSourceFlightsMutation.mutateAsync({
                    ...values,
                    target_track_id: track.id,
                });
            }
            return importSourceFlightsMutation.mutateAsync(values);
        },
        [createTrackMutation, guardServerMutation, importSourceFlightsMutation],
    );

    const handleImportTableFlights = useCallback(
        async ({ values, tableModel }) => {
            if (!guardServerMutation("导入表格航班")) {
                return null;
            }
            return importTableFlightsMutation.mutateAsync({ values, tableModel });
        },
        [guardServerMutation, importTableFlightsMutation],
    );

    const saveAsHistoryMutation = useSaveTrackAsHistory(draftId, {
        onSuccess: (track) => {
            message.success(`已保存历史快照 ${track.name || ""}`.trim());
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "保存当前轨失败"));
        },
    });

    const restoreTrackMutation = useRestoreTrack(draftId, {
        onSuccess: () => {
            message.success("已从历史快照恢复为新编辑轨");
            onRestoreTrackSuccess?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "恢复历史快照失败"));
        },
    });

    const finalizeTrackMutation = useFinalizeTrack(draftId, {
        onSuccess: (track) => {
            message.success(`已发布定稿轨 ${track?.name || ""}`.trim());
            onDraftFlightsChanged?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "定稿发布失败"));
        },
    });

    const deleteTrackMutation = useDeleteTrack(draftId, {
        onSuccess: (track, trackId) => {
            const trackType = String(track?.track_type || "").toUpperCase();
            message.success(trackType === "REFERENCE" || trackType === "LIVE_REFERENCE" ? "参考轨已移除" : "轨道已删除");
            onDeleteTrackSuccess?.(track, { trackId });
            onDraftFlightsChanged?.();
        },
        onError: (error) => {
            message.error(formatScheduleDraftError(error, "删除轨道失败"));
        },
    });

    const handleDeleteTrack = useCallback(
        async (trackId, options = {}) => {
            if (!trackId) return;
            if (!options.skipDirtyGuard && !guardServerMutation("删除轨道")) {
                return null;
            }
            return deleteTrackMutation.mutateAsync(trackId);
        },
        [deleteTrackMutation, guardServerMutation],
    );

    const handleAttachReferenceTrack = useCallback(
        (values) => {
            if (!guardServerMutation("导入/挂载")) {
                return Promise.resolve(null);
            }
            return attachReferenceTrackMutation.mutateAsync(values);
        },
        [attachReferenceTrackMutation, guardServerMutation],
    );

    const handleSaveTrackAsHistory = useCallback(
        (trackId, values) => {
            if (!guardServerMutation("保存历史快照")) {
                return Promise.resolve(null);
            }
            return saveAsHistoryMutation.mutateAsync({ trackId: trackId || effectiveEditingTrackId, values });
        },
        [effectiveEditingTrackId, guardServerMutation, saveAsHistoryMutation],
    );

    const handleRestoreTrack = useCallback(
        (trackId) => {
            if (!guardServerMutation("恢复历史快照")) {
                return Promise.resolve(null);
            }
            return restoreTrackMutation.mutateAsync(trackId);
        },
        [guardServerMutation, restoreTrackMutation],
    );

    const updateDraftMutation = useMutation(
        (values) => scheduleDraftDataService.updateDraft(draftId, values),
        buildMutationOptions({
            onSuccess: async (_draft, values) => {
                const updates = values || {};
                if (Object.prototype.hasOwnProperty.call(updates, "period_start") || Object.prototype.hasOwnProperty.call(updates, "period_end")) {
                    const draftDetailKey = scheduleDraftQueryKeys.detail(draftId);
                    await Promise.all([
                        queryClient.invalidateQueries(scheduleDraftQueryKeys.matchesRoot(draftId)),
                        queryClient.invalidateQueries([...draftDetailKey, "compare-confirmation"]),
                        queryClient.invalidateQueries([...draftDetailKey, "comparison-baseline"]),
                        queryClient.invalidateQueries(scheduleDraftQueryKeys.executionFlights()),
                    ]);
                }
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "更新草稿失败"));
            },
        }),
    );

    const triggerAgentMutation = useMutation(
        (payload) => scheduleDraftDataService.triggerAgent(draftId, payload),
        buildMutationOptions({
            invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.agentSessions(draftId), scheduleDraftQueryKeys.lists()],
            onSuccess: (result) => {
                const nextStatus = result?.agent_session_status || "AGENT_WORKING";
                queryClient.setQueryData(scheduleDraftQueryKeys.detail(draftId), (current) => (current ? { ...current, agent_session_status: nextStatus } : current));
                queryClient.setQueryData(scheduleDraftQueryKeys.agentSessions(draftId), (current) => ({
                    agent_session_status: nextStatus,
                    sessions: current?.sessions || [],
                }));
                const total = result?.total || result?.ag_request_ids?.length || 0;
                message.success(total ? `已启动 ${total} 个 Agent 编排任务` : "已启动 Agent 编排任务");
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "启动 Agent 编排失败"));
            },
        }),
    );

    const invalidateMatchQueries = useCallback(
        async (trackId, referenceTrackId) => {
            await Promise.all([
                queryClient.invalidateQueries(scheduleDraftQueryKeys.detail(draftId)),
                queryClient.invalidateQueries(scheduleDraftQueryKeys.matchesRoot(draftId)),
                trackId ? queryClient.invalidateQueries(scheduleDraftQueryKeys.matches(draftId, trackId, referenceTrackId)) : Promise.resolve(),
            ]);
        },
        [draftId, queryClient],
    );

    const ensureComparisonBaselineMutation = useMutation(
        ({ trackId }) => {
            if (!trackId) {
                throw new Error("缺少编辑轨 ID");
            }
            return scheduleDraftDataService.ensureComparisonBaseline(draftId, trackId);
        },
        {
            onSuccess: async (result, variables) => {
                await invalidateMatchQueries(variables?.trackId || result?.track_id, result?.reference_track_id || result?.referenceTrackId || result?.track?.compared_reference_track_id);
            },
        },
    );

    const bindComparisonBaselineMutation = useMutation(
        ({ trackId, referenceTrackId }) => {
            const targetTrackId = trackId || effectiveEditingTrackId;
            if (!targetTrackId || !referenceTrackId) {
                throw new Error("缺少编辑轨或对比基线 ID");
            }
            return scheduleDraftDataService.bindComparisonBaseline(draftId, targetTrackId, referenceTrackId);
        },
        {
            onSuccess: async (result, variables) => {
                await invalidateMatchQueries(variables?.trackId || result?.track_id, variables?.referenceTrackId || result?.reference_track_id || result?.track?.compared_reference_track_id);
                message.success("对比基线已切换，旧匹配已清理；请运行自动匹配预览后保存 Compare");
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "切换对比基线失败"));
            },
        },
    );

    const createMatchMutation = useMutation(
        ({ trackId, referenceTrackId, payload }) => {
            const targetTrackId = trackId || effectiveEditingTrackId;
            if (!targetTrackId) {
                throw new Error("缺少编辑轨 ID");
            }
            return scheduleDraftDataService.createMatch(draftId, targetTrackId, {
                ...payload,
                ...(referenceTrackId ? { reference_track_id: referenceTrackId } : {}),
            });
        },
        {
            onSuccess: async (match, variables) => {
                await invalidateMatchQueries(variables?.trackId || match?.track_id || effectiveEditingTrackId, variables?.referenceTrackId || match?.reference_track_id);
                message.success("匹配状态已保存");
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "保存匹配状态失败"));
            },
        },
    );

    const autoMatchMutation = useMutation(
        ({ trackId, referenceTrackId }) => {
            const targetTrackId = trackId || effectiveEditingTrackId;
            if (!targetTrackId) {
                throw new Error("缺少编辑轨 ID");
            }
            return scheduleDraftDataService.autoMatchTrack(draftId, targetTrackId, referenceTrackId);
        },
        {
            onSuccess: async (result, variables) => {
                await invalidateMatchQueries(variables?.trackId || result?.track_id || effectiveEditingTrackId, variables?.referenceTrackId || result?.reference_track_id);
                if (!variables?.silent) {
                    message.success(`自动匹配完成，生成 ${result?.created_count || 0} 条连线`);
                }
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "自动匹配失败"));
            },
        },
    );

    const deleteMatchMutation = useMutation(
        ({ trackId, referenceTrackId, matchId }) => {
            const targetTrackId = trackId || effectiveEditingTrackId;
            if (!targetTrackId || !matchId) {
                throw new Error("缺少编辑轨或匹配记录 ID");
            }
            return scheduleDraftDataService.deleteMatch(draftId, targetTrackId, matchId, referenceTrackId);
        },
        {
            onSuccess: async (_, variables) => {
                await invalidateMatchQueries(variables?.trackId || effectiveEditingTrackId, variables?.referenceTrackId);
                message.success("匹配关系已解除");
            },
            onError: (error) => {
                message.error(formatScheduleDraftError(error, "解除匹配失败"));
            },
        },
    );

    const handleUpdateDraft = useCallback(
        (values) => {
            if (!guardServerMutation("更新草稿")) {
                return Promise.resolve(null);
            }
            return updateDraftMutation.mutateAsync(values);
        },
        [guardServerMutation, updateDraftMutation],
    );

    const handleTriggerAgent = useCallback(
        (payload) => {
            if (!guardServerMutation("触发 Agent")) {
                return Promise.resolve(null);
            }
            return triggerAgentMutation.mutateAsync(payload);
        },
        [guardServerMutation, triggerAgentMutation],
    );

    const handleEnsureComparisonBaseline = useCallback(
        (trackId) => {
            if (!guardServerMutation("进入 Compare")) {
                return Promise.resolve(null);
            }
            return ensureComparisonBaselineMutation.mutateAsync({ trackId });
        },
        [ensureComparisonBaselineMutation, guardServerMutation],
    );

    const handleBindComparisonBaseline = useCallback(
        (trackId, referenceTrackId) => {
            if (!guardServerMutation("切换对比基线")) {
                return Promise.resolve(null);
            }
            return bindComparisonBaselineMutation.mutateAsync({ trackId, referenceTrackId });
        },
        [bindComparisonBaselineMutation, guardServerMutation],
    );

    const handleCreateMatch = useCallback(
        (trackId, payload, options = {}) => {
            if (!guardServerMutation("保存匹配状态")) {
                return Promise.resolve(null);
            }
            return createMatchMutation.mutateAsync({ trackId, payload, ...options });
        },
        [createMatchMutation, guardServerMutation],
    );

    const handleAutoMatch = useCallback(
        (trackId, options = {}) => {
            if (!guardServerMutation("自动匹配")) {
                return Promise.resolve(null);
            }
            return autoMatchMutation.mutateAsync({ trackId, ...options });
        },
        [autoMatchMutation, guardServerMutation],
    );

    const handleDeleteMatch = useCallback(
        (trackId, matchId, options = {}) => {
            if (!guardServerMutation("解除匹配")) {
                return Promise.resolve(null);
            }
            return deleteMatchMutation.mutateAsync({ trackId, matchId, ...options });
        },
        [deleteMatchMutation, guardServerMutation],
    );

    const handleUpdateFlight = useCallback(
        (flightId, values) => {
            if (!guardServerMutation("更新航班")) {
                return Promise.resolve(null);
            }
            return updateFlightMutation.mutateAsync({ flightId, values });
        },
        [guardServerMutation, updateFlightMutation],
    );

    const handleMoveFlight = useCallback(
        (flightId, values) => {
            if (!guardServerMutation("移动航班")) {
                return Promise.resolve(null);
            }
            return moveFlightMutation.mutateAsync({ flightId, values });
        },
        [guardServerMutation, moveFlightMutation],
    );

    const handleAddFlight = useCallback(
        async (values = {}) => {
            if (!draftId || !editPipeline?.executeCommand) {
                throw new Error("缺少本地编辑 Pipeline");
            }

            const trackId = values.track_id || values.trackId || effectiveEditingTrackId;
            if (!trackId) {
                throw new Error("缺少编辑轨 ID");
            }

            const flightId = values.id || createClientFlightId();
            const normalizedValues = normalizeDateTimeValues(values);
            const insertionContext = values.insertionContext || values.insertion || (values.mode && values.anchorFlight ? {
                mode: values.mode,
                anchorFlight: values.anchorFlight,
            } : null);
            const payload = {
                ...buildScheduleDraftFlightPayload(normalizedValues),
                id: flightId,
                track_id: trackId,
            };

            setAddingLocalFlight(true);
            try {
                const result = await editPipeline.executeCommand({
                    type: ADD_FLIGHT,
                    draftId,
                    flightId,
                    payload,
                    meta: {
                        source: "add-flight",
                        trackId,
                        ...(insertionContext ? { insertionContext } : {}),
                    },
                });

                if (result?.status !== "success") {
                    message.warning(getFirstBlockingMessage(result, "新增航班未应用"));
                    return result;
                }

                const localFlight = { ...payload, change_status: "ADDED", change_source: payload.change_source || "USER" };
                result?.changeSet?.changes?.forEach((change) => {
                    if (String(change.flightId ?? change.flight_id) === String(flightId) && change.field) {
                        localFlight[change.field] = change.after;
                    }
                });
                message.success(`已新增航班 ${localFlight.flight_number || ""}`.trim());
                onAddFlightSuccess?.(localFlight, values);
                return localFlight;
            } catch (error) {
                message.error(formatScheduleDraftError(error, "新增航班失败"));
                throw error;
            } finally {
                setAddingLocalFlight(false);
            }
        },
        [draftId, editPipeline, effectiveEditingTrackId, onAddFlightSuccess],
    );

    const handleDeleteFlight = useCallback(
        async (flightId) => {
            if (!draftId || !editPipeline?.executeCommand) {
                throw new Error("缺少本地编辑 Pipeline");
            }
            if (flightId === undefined || flightId === null) {
                return null;
            }

            setDeletingLocalFlight(true);
            try {
                const result = await editPipeline.executeCommand({
                    type: DELETE_FLIGHT,
                    draftId,
                    flightId,
                    payload: {
                        change_status: "DELETED",
                    },
                    meta: {
                        source: "delete-flight",
                    },
                });

                if (result?.status !== "success") {
                    message.warning(getFirstBlockingMessage(result, "删除航班未应用"));
                    return result;
                }

                message.success("航班已标记删除");
                onDeleteFlightSuccess?.();
                return result;
            } catch (error) {
                message.error(formatScheduleDraftError(error, "删除航班失败"));
                throw error;
            } finally {
                setDeletingLocalFlight(false);
            }
        },
        [draftId, editPipeline, onDeleteFlightSuccess],
    );

    return {
        handleAddFlight,
        handleUpdateFlight,
        handleDeleteFlight,
        handleMoveFlight,
        handleAttachReferenceTrack,
        handleImportSourceFlights,
        handleImportTableFlights,
        handleSaveTrackAsHistory,
        handleRestoreTrack,
        handleFinalizeTrack: (trackId) => finalizeTrackMutation.mutateAsync(trackId || effectiveEditingTrackId),
        handleDeleteTrack,
        handleUpdateDraft,
        handleTriggerAgent,
        handleEnsureComparisonBaseline,
        handleBindComparisonBaseline,
        handleCreateMatch,
        handleAutoMatch,
        handleDeleteMatch,
        addingFlight: addingLocalFlight,
        updatingFlight: updateFlightMutation.isLoading,
        updatingDraft: updateDraftMutation.isLoading,
        deletingFlight: deletingLocalFlight,
        movingFlight: moveFlightMutation.isLoading,
        attachingReferenceTrack: attachReferenceTrackMutation.isLoading,
        importingSourceFlights: importSourceFlightsMutation.isLoading,
        importingTableFlights: importTableFlightsMutation.isLoading,
        savingTrackAsHistory: saveAsHistoryMutation.isLoading,
        restoringTrack: restoreTrackMutation.isLoading,
        restoringTrackId: restoreTrackMutation.variables,
        finalizingTrack: finalizeTrackMutation.isLoading,
        finalizeTrackError: finalizeTrackMutation.error,
        triggeringAgent: triggerAgentMutation.isLoading,
        ensuringComparisonBaseline: ensureComparisonBaselineMutation.isLoading,
        bindingComparisonBaseline: bindComparisonBaselineMutation.isLoading,
        creatingMatch: createMatchMutation.isLoading,
        autoMatching: autoMatchMutation.isLoading,
        deletingMatch: deleteMatchMutation.isLoading,
        deletingTrack: deleteTrackMutation.isLoading,
        deletingTrackId: deleteTrackMutation.variables,
    };
}

export default useScheduleDraftEditorMutations;
